import { uniq } from 'lodash'
import { AutoComplete, AutoCompleteProps } from 'primereact/autocomplete'
import { classNames } from 'primereact/utils'
import { useRef, useState } from 'react'
import { useCursorPreserver } from '../../../../../../../hooks/useCursorPreserver'
import { AzureADUserModel } from '../../../../../../../models/AzureADUserModel'
import { AzureADService } from '../../../../../../../services/AzureADService'
import styles from './UsersAutoComplete.module.scss'
import GMAWBUsersService from '../../../../../../../services/GMAWBUsersService'

type Props = {
  valueType?: 'string' | 'object'
  autoFitOptionsWidth?: boolean
  autoClearInputOnBlur?: boolean
  minSearchInputLength?: number
  source: 'azure-ad' | 'gmawb-users'
} & Omit<AutoCompleteProps, 'suggestions' | 'completeMethod'>

const UsersAutoComplete = (props: Props) => {
  const {
    valueType = 'string',
    autoFitOptionsWidth = true,
    autoClearInputOnBlur = false,
    minSearchInputLength = 3,
    ...autoCompleteProps
  } = props
  const { ref: inputRef, handleChange } = useCursorPreserver(props.value, props.onChange)
  const autoCompleteRef = useRef<AutoComplete>(null)
  const [suggestions, setSuggestions] = useState<any[]>([])
  const invalidSearchInputMessage = `Enter at least ${minSearchInputLength} characters to search`

  const handleCompleteMethod = async (e: { query: string }) => {
    e.query = e.query || props.value || ''

    if (e.query.length < minSearchInputLength) {
      setSuggestions([invalidSearchInputMessage])
      return
    }

    if (props.source === 'gmawb-users') {
      const response = await GMAWBUsersService.getPaginatedUsers(0, 50, undefined, false, e.query)
      let users = response.data

      if (props.value && Array.isArray(props.value)) {
        const existingEmails = props.value.map(v => (typeof v === 'string' ? v : v.mail))
        users = users.filter(user => !existingEmails.includes(user.normalizedEmail))
      }

      const mappedSuggestions =
        users?.map(user => {
          const parts = [user.displayName]
          parts.push(user.normalizedEmail)
          const field = parts.join(' - ')

          return valueType === 'string' ? field : { ...user, field }
        }) ?? []

      setSuggestions(uniq(mappedSuggestions))
      return
    }

    const response = (await AzureADService.searchUsers(e.query)) as any
    let users = response.data as AzureADUserModel[]

    if (props.value && Array.isArray(props.value)) {
      const existingEmails = props.value.map(v => (typeof v === 'string' ? v : v.mail))
      users = users.filter(user => !existingEmails.includes(user.mail))
    }

    const mappedSuggestions =
      users?.map(user => {
        const parts = [user.displayName]
        const employeeNumber = user.userPrincipalName.split('@')[0]
        if (!isNaN(employeeNumber as any)) {
          parts.push(employeeNumber)
        }
        parts.push(user.mail)
        const field = parts.join(' - ')

        return valueType === 'string' ? field : { ...user, field }
      }) ?? []

    setSuggestions(uniq(mappedSuggestions))
  }

  const handleBlur = (e: React.FocusEvent<HTMLInputElement, Element>) => {
    if (autoClearInputOnBlur && autoCompleteRef.current) {
      autoCompleteRef.current.getInput().value = ''
    }

    props.onBlur?.(e)
  }

  return (
    <AutoComplete
      {...autoCompleteProps}
      ref={autoCompleteRef}
      inputRef={inputRef as React.Ref<HTMLInputElement>}
      onChange={handleChange}
      onBlur={handleBlur}
      className={classNames(
        suggestions[0] === invalidSearchInputMessage && styles.invalidSearchInput,
        props.className
      )}
      panelClassName={classNames(
        autoFitOptionsWidth && '[&>*]:w-fit',
        suggestions[0] === invalidSearchInputMessage &&
          props.value?.length >= minSearchInputLength &&
          'invisible'
      )}
      completeMethod={handleCompleteMethod}
      delay={1000}
      suggestions={suggestions}
      minLength={minSearchInputLength}
      field={valueType === 'string' ? undefined : 'field'}
    />
  )
}

export default UsersAutoComplete
